import { API_BASE } from './api-base';
import { apiFetch, apiFetchStream } from './api-client';
import { conversationCreateBody } from './conversation-space';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type {
  ConnectorResultCardMetadata,
  CreditGiftCardMetadata,
  InsightCardMetadata,
  MeetingNotesCardMetadata,
  MessageAttachment,
  MessageReferences,
  MilestoneCardMetadata,
  ProductCardsMetadata,
} from '@wix/ai-scheduling-assistant-common-lib/entities';
import { flushSuggestedTaskAck } from './suggested-task-ack';
import { createSseParser } from './sse-parser';
import { useDuplexer, DuplexerEntity, duplexerAttachments } from './use-duplexer';
import {
  OWN_REACTION_BY,
  patchAttachmentsById,
  reactionAnsweredRow,
  setReactionsById,
  toggleReaction as toggleReactionEntry,
  upsertMessageById,
  type MessageReaction,
} from './message-reactions';
import { isOrphanedStreamBlock, isStreamedDuplicate } from './message-dedup';
import { useRemoteTurn } from './use-remote-turn';
import { useDevPictureArrival } from './dev-picture-arrival';
import type { TurnActivitySignal } from './turn-activity';
import { bumpConversation } from './conversations';
import { patchChoicesById, readChoiceAnswer, readMessageChoices, type ChoiceAnswer, type MessageChoices } from './message-choices';

/**
 * The chat cards this web build can draw, sent on every user message so the harness knows which shapes
 * are worth sending. `share-someone-else`: a share offer's "Send to someone else" — without it the
 * harness withholds a share card that has no names on it, because a build that cannot draw the choice
 * would show a card with nothing to click. Mirrors common-lib's CLIENT_CAPABILITY_SHARE_SOMEONE_ELSE
 * (this package cannot import common-lib from `lib/`). `invite-card` / `pack-share-card`: this build draws
 * `inviteCard` and `packShareCard`, so the harness may spend their one-time asks on this reply.
 */
export const WEB_CLIENT_CAPABILITIES = ['share-someone-else', 'invite-card', 'pack-share-card'] as const;
import { browserTimeZone } from './browser-time-zone';
import { patchSurveyById, readMessageSurvey, type MessageSurvey, type SurveyAnswer } from './message-survey';
import { ChatCards, isFirstWorkspaceMilestone, patchChatCardsById, readChatCards } from './chat-cards';
import type { SendHoldCard } from './send-hold';
import { type ConnectorCardData } from './connector-card';
import { type ProfileLinkCardData } from './profile-link-chat-card';
import { type OpportunityCardData } from './opportunity-card';
import { type OutreachPreviewCardData } from './outreach-preview-card';
import { type ShareOfferCardData, type ShareResponseRowData } from './share-offer-card';
import type { MeetingApprovalMarker } from './meeting-approval';
import { liveChatRowKind, ownUnconfirmedSendId } from './live-chat-row';
import { readMeetingRoomFlag, readSpokenAloud } from './meeting-room-rows';
import type { ProposalsCardMarker } from './proposals-card';
import { type PostConnectedConnectorCardData } from './post-connected-connector-card';
import { type BandCardData, type BandOfferCardData } from './band-cards';
import type { GoalCardData } from './goal-card';
import { type CreditsCardData } from './credits-card';
import { type InviteCardData } from './invite-card';
import { type PackShareChatCardData } from './pack-share-chat-card';
import { reportAppsFlyerOnboardingComplete, reportAppsFlyerSend } from './appsflyer';
import { ensureMe } from './me';
import { clearLandingIntent, loadLandingIntent } from './landing-intent';
import { hasFired, reportFunnelSend } from './gtm-funnel';
import { parseChannelReadOnlyError, type ExternalChannel } from './external-channel';
import { createSingleFlight } from './single-flight';
import { readMeetingSummaryFlag } from './brief-summary-flag';
import { readSystemErrorFlag } from './failed-turn';
import { readOutreachReplyScope } from './outreach-retry-scope';
import { readOutreachComposerMarker, type OutreachComposerMarker } from './outreach-lane';
import { restoreSentRow } from './turn-seed-repair';
import { useChatHistory, type ChatHistoryOptions } from './use-chat-history';

// Streaming (and Stop) go through the orion-web API route, like every other
// conversation call. That route resolves + signs the caller's Orion metasite
// server-side (the harness needs it to scope the conversation / KV stores) and
// injects SSE keep-alive heartbeats so an idle-sensitive proxy doesn't cut long
// agentic turns short during the quiet gaps between tool steps.

export type Message = {
  id?: string;
  conversationId?: string;
  role?: string;
  content?: string;
  toolName?: string;
  toolInput?: string;
  createdDate?: string;
  // Local-only: set true when the bubble was born from a `messageStart` event in
  // this session, so the typewriter knows to animate. Loaded-from-history
  // messages don't carry this flag and render immediately.
  animate?: boolean;
  // Canonical persisted id captured at `messageEnd`. We keep the streamId as
  // the React key so the typewriter doesn't snap-remount, so we need this
  // field separately to dedupe against duplexer-pushed entities (which
  // reference the persisted id).
  finalId?: string;
  // Local-only: TRUE between this row's `messageStart` and its `messageEnd` — i.e. while the server is
  // still appending deltas to it. It is what the streaming caret is keyed on.
  //
  // The caret used to be keyed on `displayed.length < content.length` (the typewriter having more to
  // reveal), which is a DIFFERENT question and flaps: the typewriter runs at 212-515 chars/s and the
  // SSE feed is slower, so it drains the buffer and catches up between almost every delta. The caret
  // therefore mounted and unmounted tens of times a second, and — because it renders after a BLOCK
  // element — each toggle added or removed a whole 24px line box, jerking the bottom-pinned transcript
  // by that much. See `AssistantMarkdownContent`.
  //
  // NOT derived from `!finalId`: `messageEnd` may arrive with no persisted id (`finalId` is optional in
  // the event). Cleared by its own `messageEnd`, or by the NEXT block opening — a row whose End never
  // lands would otherwise stay "open" for the session and take a caret back on the following turn (the
  // automated review's finding on #9647). On top of that the row only wears the caret while it is the
  // panel's newest row and a turn is live. See `MessageRow`'s `turnActive`.
  streamOpen?: boolean;
  attachments?: MessageAttachment[];
  // Set when the row is an "agent joined your team" card (harness stamps
  // `metadata.agentCard`). Read once, at message-construction time, via the
  // shared `readAgentCard` helper so the render path just checks this marker.
  // Populated on the duplexer push here and on history load (messages API).
  // `id` is the hired agent's stable id — the key for its identity color
  // (`colorForAgent`), so the join card's node matches the same agent's hue
  // everywhere. Optional: legacy cards persisted before the id existed fall
  // back to the name.
  agentCard?: { id?: string; name: string; title?: string };
  // Set when the row is a live "agent at work" card (harness stamps
  // `metadata.agentAtWorkCard` when the Maestro spawns an agent onto a task).
  // Read once via the shared `readAgentAtWorkCard` helper, same as `agentCard`.
  //
  // `taskId` is the only real state here: the card reads the task's live status,
  // title, action sentence, credit total and BOTH run conversations off it. The
  // rest are first-frame hints for the moment before that read lands, and a
  // reader must always prefer the live task — see `AgentAtWorkCardMetadata` in
  // common-lib for why nothing that moves may be persisted on a history row.
  agentAtWorkCard?: { taskId: string; agentId?: string; agentName?: string; agentTitle?: string; taskTitle?: string };
  // Set when the row is a setup-milestone card (harness stamps
  // `metadata.milestoneCard` — a workspace created, a routine gone live, …).
  // Read once via the shared `readMilestoneCard` helper so the render path just
  // checks this marker. Populated on the duplexer push here and on history load
  // (messages API), mirroring `agentCard`. `nav` is an optional in-app CTA
  // destination (e.g. the dashboard card → home), resolved per-surface.
  milestoneCard?: MilestoneCardMetadata;
  /** Present on the synthetic row announcing a goodwill credit gift (`metadata.creditGiftCard`). */
  creditGiftCard?: CreditGiftCardMetadata;
  // The sub-agent that authored this turn (harness stamps `metadata.agentId` on
  // its round-table reports). Present => render as an attributed group-chat row
  // (avatar via `colorForAgent` + name/title from the roster); absent => the
  // Maestro's own turn. Populated on the duplexer push here and on history load
  // (messages API), so a brief thread reads like a team standup.
  agentId?: string;
  // The brief round-table's wrap-up turn (harness stamps `metadata.agentMeetingSummary`).
  // Present+true => render as the MeetingSummaryCard (title · date/time + agents-met cluster ·
  // markdown bullets) in place of a bubble, matching iOS/Android. Populated on the duplexer push
  // here and on history load (messages API); tolerant of a JSON bool OR a `"true"` string.
  agentMeetingSummary?: boolean;
  // Quoted-reply reference (metadata.replyTo) — renders as a quote block above the bubble.
  // WhatsApp-originated replies carry waMessageId; in-app replies carry messageId.
  replyTo?: { waMessageId?: string; messageId?: string; preview?: string; sender?: string };
  // Emoji reactions (metadata.reactions) — one entry per person per emoji; the
  // caller's own entries carry by === 'user'. Populated on the duplexer push
  // here and on history load (messages API), mirroring replyTo.
  reactions?: MessageReaction[];
  // Structured ask (metadata.choices) on an ASSISTANT row — renders as choice
  // chips under the body. Populated on the duplexer push here and on history
  // load (messages API), mirroring replyTo/reactions.
  choices?: MessageChoices;
  // The structured answer a USER turn carries (metadata.choiceAnswer / the
  // optimistic bubble's send argument) — selection state for the matching
  // ask's chips derives from these rows.
  choiceAnswer?: ChoiceAnswer;
  // Structured multi-question ask (metadata.survey) on an ASSISTANT row — renders
  // as the paginated survey card under the body. Populated on the duplexer push
  // here, on history load (messages API), and in-band at messageEnd, mirroring choices.
  survey?: MessageSurvey;
  // The submitted survey a USER turn carries (metadata.surveyAnswer / the optimistic
  // bubble's send argument) — the matching survey card's settled selection derives
  // from these rows, mirroring choiceAnswer.
  surveyAnswer?: SurveyAnswer;
  /** Incoming thread selection for URL-handoff dedup; null preserves an invalid persisted value. */
  outreachId?: string | null;
  /** A "Tell {agent}" composer note (metadata.outreachComposer on a USER row) — renders the thread chip. */
  outreachComposer?: OutreachComposerMarker;
  // Connector connect card (metadata.connectorCard) on an ASSISTANT reply whose
  // turn minted a Composio OAuth link — renders the connect card under the body
  // (the reply text deliberately carries no URL). Populated on the duplexer push
  // here, on history load (messages API), and in-band at messageEnd, mirroring
  // choices.
  connectorCard?: ConnectorCardData;
  /**
   * "Your Symphony card" (metadata.profileLinkCard) — the profile link the turn claimed, drafted or changed, with
   * its Open / Share / Edit. Rides the reply like `connectorCard`, under the words; see `lib/profile-link-chat-card.ts`.
   */
  profileLinkCard?: ProfileLinkCardData;
  /** The low-credits nudge under the reply — rides the reply like `connectorCard`. */
  creditsCard?: CreditsCardData;
  /** The owner's own invite link at a good moment — rides the reply like `creditsCard`; see `lib/invite-card.ts`. */
  inviteCard?: InviteCardData;
  /** "Share this pack" on the reply of the turn that created it — rides the reply; see `lib/pack-share-chat-card.ts`. */
  packShareCard?: PackShareChatCardData;
  /**
   * The opportunity suggestion under the reply — chip, win, how. Rides the reply like
   * `connectorCard`, and its two CTAs are this same row's `choices`.
   */
  opportunityCard?: OpportunityCardData;
  insightCard?: InsightCardMetadata;
  /** Product details for a shopping reply's pictures; each card is drawn in place of its picture. */
  productCards?: ProductCardsMetadata;
  connectorResultCard?: ConnectorResultCardMetadata;
  /**
   * What each contact will receive, on the reply that asks the user to approve an outreach — rides
   * the reply like `connectorCard`, and its actions are this same row's `choices`.
   */
  outreachPreviewCard?: OutreachPreviewCardData;
  /**
   * The share offer — the card as the recipient will meet it, with the name chips that SEND on a tap.
   * Rides the reply like `connectorCard`, but unlike the outreach preview its actions are NOT this
   * row's `choices`: a chip carries a person id, and a choice chip carries only a label.
   */
  shareOfferCard?: ShareOfferCardData;
  /**
   * A recipient reacted. This one is NOT a card riding a reply — for a bare emoji the design gives the
   * response a templated row and no agent turn at all, so the row's whole content is this marker.
   */
  shareResponseCard?: ShareResponseRowData;
  /**
   * What a connected app brought them, with a switch per automation — rides the reply like
   * `connectorCard`. Its opposite number: that one offers a connection, this describes one that
   * already happened.
   */
  postConnectedConnectorCard?: PostConnectedConnectorCardData;
  shopifyBatchCard?: import('@wix/ai-scheduling-assistant-common-lib/entities').ShopifyBatchCard;
  shopifyBriefingCard?: import('@wix/ai-scheduling-assistant-common-lib/entities').ShopifyBriefingCard;
  /**
   * Two whole bands to choose between — rides the reply like `connectorCard`, and the pick is this same
   * row's `choices` chips, so the card itself carries no action.
   */
  bandOfferCard?: BandOfferCardData;
  /**
   * The band as created, with every app and where each one stands. Rides the reply like
   * `connectorCard`; its rows expand to the same live routine switches the post-connected card draws.
   */
  bandCard?: BandCardData;
  /** A live view of one goal — see `lib/goal-card.ts`. Rides the reply like `bandCard`. */
  goalCard?: GoalCardData;
  /** The reply of the turn that created a band. Draws nothing; see `isFirstWorkspaceMilestone`. */
  bandCreated?: true;
  /**
   * A video meeting's write-up, as facts rather than prose: the meeting's OWN start and end (never the
   * time the write-up landed), the link, who attended and who spoke. The card's body is the `notes.md`
   * attached to the same row, so an edit to the file changes the card without touching this row.
   */
  meetingNotesCard?: MeetingNotesCardMetadata;
  /** A USER row the room said to the agent during a video meeting — see `lib/meeting-room-rows.ts`. */
  fromMeetingRoom?: boolean;
  /** An ASSISTANT row someone HEARD (`metadata.voiceTranscript`). In a meeting room's span it is the room's
   *  answer, never the owner's reply — see `lib/meeting-room-rows.ts`. */
  spokenAloud?: boolean;
  /** The approval ask the harness wrote into the owner's approval thread — see `lib/meeting-approval.ts`. */
  meetingApproval?: MeetingApprovalMarker;
  /** The proposed tasks this reply offers for approval — see `lib/proposals-card.ts`. */
  proposalsCard?: ProposalsCardMarker;
  /**
   * Messages this turn left WAITING to go out (the send rule) — each with its Cancel. Rides the reply like
   * `connectorCard`; see `lib/send-hold.ts` and `native-apps/shared/send-hold/contract.json`.
   */
  sendHoldCard?: SendHoldCard;
  /** The meeting summary's waiting messages (`metadata.meetingHoldsCard`, meeting hold M6): Cancel and Send now. */
  meetingHoldsCard?: SendHoldCard;
  /**
   * The canned apology a FAILED turn persists (`metadata.systemError`, harness `persistTurnError`). Renders
   * as the failed-turn row — localized copy and "Try again" — instead of the English apology as a bubble.
   * Populated on history load (messages API) and on the duplexer push. Shared 1:1 with iOS/Android
   * `ChatMessage.systemError`; see `lib/failed-turn.ts`.
   */
  systemError?: boolean;
};

export type PendingAttachment = {
  id: string;
  file: File;
  previewUrl?: string;
  status: 'uploading' | 'done' | 'error';
  attachment?: MessageAttachment;
  error?: string;
  errorKind?: 'unsupported' | 'too_large' | 'failed';
};

export interface ChatState {
  history?: ReturnType<typeof useChatHistory>;
  conversationId: string | null;
  loading: boolean;
  /**
   * The selected conversation's server read has LANDED (or failed) for this selection — a cached paint,
   * which clears `loading` at once, does not count. The open-at-message search (`lib/open-target-search.ts`)
   * waits on it before deciding a target is not there.
   */
  readSettled: boolean;
  streaming: boolean;
  /**
   * A turn is running that this client did NOT start — a pack accept, a pulse, a brief, an agent
   * report. Read off the conversation's `pendingPredictionId` (see `turn-activity.ts`), because
   * `streaming` only ever describes our own send: without this, a server-started turn rendered as a
   * dead screen for its whole duration and the user learned about it from a push.
   */
  remoteTurnActive: boolean;
  error: string | null;
  /**
   * Set when a send bounced off the server-side channel guard with its typed 403
   * (`errorCode: CONVERSATION_CHANNEL_READ_ONLY`) — the conversation lives on
   * WhatsApp/SMS and the web composer must lock. Backstop for stale client
   * state; normally the sidebar DTO's communicationChannel locks the composer
   * before a send is even possible. Cleared on conversation switch.
   */
  readOnlyChannel: ExternalChannel | null;
  messages: Message[];
  /**
   * The survey the CURRENT turn has already offered but has not yet stamped on a row — delivered
   * ahead of the reply it belongs to, so the card can take the composer's place as soon as the ask
   * exists rather than at the end of the turn. A surface renders it only while no row carries the same
   * `askId`; see the state's own comment for the hand-over and for when it is dropped.
   */
  earlySurvey?: MessageSurvey;
  /**
   * The model is typing a survey's questions right now. A surface keeps its ordinary in-flight
   * indicator up while this is true, even though the turn's reply already reads as finished — see the
   * state's own comment for why that window is otherwise dead air.
   */
  surveyWriting?: boolean;
  send: (
    text: string,
    attachments?: MessageAttachment[],
    references?: MessageReferences,
    replyTo?: Message['replyTo'],
    choiceAnswer?: ChoiceAnswer,
    /**
     * Hidden per-send context: prompt context for THIS turn, never shown as the user's words and never
     * persisted onto the message row. Distinct from `references`, which the harness lifts onto the row's
     * metadata. This is the harness's top-level `context` field, the channel the native apps have always
     * used for the same job (iOS/Android `sendUserMessage(context:)`).
     */
    context?: string,
    /**
     * The submitted inline survey — threaded exactly like `choiceAnswer`: stamped on the optimistic
     * USER bubble so the card settles at once, and sent in the stream body so the harness stamps it on
     * the persisted row's `metadata.surveyAnswer`. The readable answer travels as `text`, same as a
     * tapped chip's label.
     */
    surveyAnswer?: SurveyAnswer,
    /**
     * The Space this send's NEW conversation belongs to, stamped at creation as `metadata.workspaceId`
     * (`lib/conversation-space.ts`). Ignored when a conversation is already open: a Space is set once.
     */
    workspaceId?: string,
  ) => void;
  /**
   * Mid-stream steer: send a message WHILE a turn is streaming. The row is persisted via the
   * push endpoint (no new stream) and the harness folds it into the running turn, which keeps
   * responding. Requires a live conversation — a no-op before one exists. Does NOT touch
   * `streaming`; the in-flight turn owns it.
   */
  steer: (text: string, attachments?: MessageAttachment[]) => void;
  stop: () => void;
  /** Returns the active conversation id, creating the conversation first if none is open. */
  ensureConversation: (workspaceId?: string) => Promise<string | null>;
  /** Append an already-persisted message (dedups against ids we have). */
  appendMessage: (message: Message) => void;
  /** Re-sync the active conversation from the server (used after a voice call ends). */
  reload: () => Promise<void>;
  /** Toggle the user's own emoji reaction on a message — optimistic, reconciled from the server echo. */
  toggleReaction: (message: Message, emoji: string) => void;
  /** Proactive onboarding kickoff: streams the assistant's opening greeting live (typing indicator +
   *  token-by-token), binding to the conversation the harness creates. No-ops (returns null) for the
   *  control arm / already-onboarded users. Returns the new conversation id when it greeted. */
  startOnboarding: () => Promise<string | null>;
}

interface StreamEventEnvelope {
  type: string;
  data: any;
}

// Shared in-memory cache of loaded conversations, keyed by conversation id. A
// second useChat mount for the same thread — e.g. expanding the floating chat
// into the full chat — seeds from this instantly (no loader flash) and just
// revalidates in the background, instead of re-fetching from scratch. Lives for
// the page session; cleared on reload.
const messageCache = new Map<string, Message[]>();

export function useChat(selectedId: string | null, onConversationCreated: (id: string) => void, onTurnComplete: () => void,
  historyOptions?: ChatHistoryOptions): ChatState {
  const [conversationId, setConversationId] = useState<string | null>(selectedId);
  // Seed from the shared cache when we've loaded this thread before, so an
  // expand (or any re-mount) paints instantly instead of flashing the loader.
  const [messages, setMessages] = useState<Message[]>(() => (selectedId && !historyOptions?.enabled ? messageCache.get(selectedId) ?? [] : []));
  // Initial loading reflects whether we're going to fetch history on mount —
  // if a selectedId is provided, the [selectedId] effect below will fetch.
  // Starting at false would let autosend fire before history is loaded, with
  // no way to detect a duplicate prefill against the existing last USER
  // message. But a cached thread is already on screen, so it loads silently.
  const [loading, setLoading] = useState<boolean>(selectedId !== null && !messageCache.has(selectedId));
  const [streaming, setStreaming] = useState(false);
  // The conversation's pending-turn state as of the last messages fetch — the seed for the busy
  // indicator of a turn this client did NOT start. Kept live after that by the duplexer.
  const [turnSeed, setTurnSeed] = useState<TurnActivitySignal | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The selection whose legacy (chats-off) read has LANDED, success or failure — what `readSettled`
  // reports on that path. A cached paint sets `loading` false at once, so `loading` cannot say this.
  const [legacyReadFor, setLegacyReadFor] = useState<string | null>(null);
  const [readOnlyChannel, setReadOnlyChannel] = useState<ExternalChannel | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Cards the turn produced before the reply that announces them started streaming — handed to the
  // next block that opens, so a card never appears above text the reader already watched type out.
  // Turn-scoped: reset per send, and each key is dropped once its own row settles carrying it.
  const turnCardsRef = useRef<{ cards: ChatCards; adopted?: string[] }>({ cards: {} });
  // The survey the turn has ALREADY offered but not yet stamped on a reply row — handed over the
  // moment the model's OfferSurvey call is complete on the wire (harness AssistantStreamEventType.
  // SURVEY), which is one DB write earlier than `messageEnd` on the ordinary path and a whole model
  // iteration earlier when the offer arrives before the reply. The card takes the composer's place,
  // so that gap is the reader sitting in front of a finished answer with nothing to do — the beat the
  // review called a risk of abandonment.
  //
  // Turn-scoped, like the cards above: a surface renders it as the pending ask, and the moment a row
  // carries the same `askId` (messageEnd's in-band copy) the ROW owns it. The two are the SAME ask —
  // the harness derives `askId` from the OfferSurvey tool-use id in both places — so the hand-over
  // needs no reconciliation. Cleared when the stream closes: by then the ask is either on a row, or it
  // was one the turn abandoned (chips outranked it, or real work followed the offer) and must not be
  // left holding the composer.
  const [earlySurvey, setEarlySurvey] = useState<MessageSurvey | undefined>(undefined);
  // The model has started WRITING a survey and has not finished. The questions stream as partial JSON
  // the model types out after the reply text is already on screen — a few hundred tokens for an
  // ordinary card — and the panel's in-flight loader is hidden by then, because a reply with content
  // has arrived. That is right for every other turn and exactly wrong for this one: the answer looks
  // finished while the thing the reader is meant to act on is still seconds away, with nothing saying
  // so. This keeps the ordinary thinking indicator up for that window. Cleared when the finished ask
  // arrives, and at stream close so it can never outlive the turn that was writing.
  const [surveyWriting, setSurveyWriting] = useState(false);
  // One handler for both arms of the survey event, because they are two states of one thing: the ask
  // arriving ENDS the writing it was announced by, and letting the two land independently is how a
  // loader gets left running under a card that is already on screen.
  const onSurveyEvent = useCallback((event: { survey?: MessageSurvey; writing?: boolean }) => {
    if (event.writing !== undefined) {
      setSurveyWriting(event.writing);
      return;
    }
    if (!event.survey) return;
    setSurveyWriting(false);
    setEarlySurvey(event.survey);
  }, []);
  // Bumped by every turn the caller starts. The post-stream reconciliation captures it and adopts the
  // server's thread only while it still matches — otherwise a slow reconciliation from the previous
  // turn would replace the message list wholesale and take a newer, optimistic turn out with it.
  const turnEpochRef = useRef(0);
  // Tracks the latest internal id, read by the duplexer + SSE handlers.
  const conversationIdRef = useRef<string | null>(conversationId);
  conversationIdRef.current = conversationId;
  const history = useChatHistory(historyOptions, selectedId, streaming, messages, setMessages, setLoading, setError, setTurnSeed);
  useDevPictureArrival(setMessages);
  const historyRef = useRef(history);
  historyRef.current = history;
  // Set when send() lazy-creates a conversation so the upcoming selectedId
  // change (from the parent catching up) can bail the message-load effect —
  // otherwise it'd abort the in-flight stream and clear the optimistic
  // user bubble. Initial mounts don't set this, so URL-driven loads
  // (e.g., /?conversationId=X) always fetch. Using an explicit signal
  // here rather than a "first run" flag also survives React StrictMode's
  // dev double-invocation of effects.
  const expectedSelectedIdAfterCreateRef = useRef<string | null>(null);
  // StrictMode dev-only double-invokes every effect. The mount-time first
  // pass runs `setMessages([])`; an autosend's optimistic user bubble that
  // queues `setMessages(prev => [...prev, optimistic])` BETWEEN the two
  // passes gets wiped by the second pass's repeat reset. Tracking the last
  // selectedId we actually processed makes the effect idempotent so the
  // second pass bails out and the optimistic message survives.
  // Sentinel ({}) so an initial selectedId of null still triggers one real
  // pass instead of being mistaken for "already processed".
  const PROCESSED_SENTINEL = useRef<unknown>({});
  const lastProcessedSelectedIdRef = useRef<string | null | unknown>(PROCESSED_SENTINEL.current);
  const owner = historyOptions?.owner ?? null;
  const previousSelection = useRef({ id: selectedId, owner, historyEnabled: historyOptions?.enabled === true });

  useEffect(() => {
    const previous = previousSelection.current;
    const ownerChanged = previous.owner !== owner;
    previousSelection.current = { id: selectedId, owner, historyEnabled: historyOptions?.enabled === true };
    if (historyOptions?.enabled) {
      // If the rollout turns off, load this same selection through the legacy path again.
      lastProcessedSelectedIdRef.current = PROCESSED_SENTINEL.current;
      if (ownerChanged) {
        abortRef.current?.abort();
        setStreaming(false);
        expectedSelectedIdAfterCreateRef.current = null;
      } else if (expectedSelectedIdAfterCreateRef.current === selectedId && selectedId) {
        expectedSelectedIdAfterCreateRef.current = null;
      } else if (conversationIdRef.current !== selectedId) {
        abortRef.current?.abort();
        setStreaming(false);
      }
      setConversationId(selectedId);
      setReadOnlyChannel(null);
      return;
    }
    // Revoking history/read features does not revoke the existing conversation stream. Its
    // post-turn reconciliation reads historyRef and will use the legacy message endpoint.
    if (previous.historyEnabled && !ownerChanged && previous.id === selectedId
      && conversationIdRef.current === selectedId && streamingRef.current) {
      lastProcessedSelectedIdRef.current = selectedId;
      setLegacyReadFor(selectedId);
      return;
    }
    if (ownerChanged) {
      lastProcessedSelectedIdRef.current = PROCESSED_SENTINEL.current;
      expectedSelectedIdAfterCreateRef.current = null;
    }
    if (lastProcessedSelectedIdRef.current === selectedId) return;
    lastProcessedSelectedIdRef.current = selectedId;
    if (expectedSelectedIdAfterCreateRef.current !== null && expectedSelectedIdAfterCreateRef.current === selectedId) {
      expectedSelectedIdAfterCreateRef.current = null;
      // This client just created it: the thread on screen IS the live one.
      setLegacyReadFor(selectedId);
      return;
    }
    // When selectedId is null and conversationId is already null, this effect's
    // resets (setMessages([]), setStreaming(false)) are no-ops — the state is
    // already empty. Skipping them here prevents a child component's autosend
    // effect (which fires first in the same commit, per React's child-before-parent
    // ordering) from having its optimistic setMessages/setStreaming queued updates
    // overwritten by this parent-hook effect running after it in the same batch.
    if (!selectedId && !conversationIdRef.current) return;
    abortRef.current?.abort();
    setError(null);
    setReadOnlyChannel(null);
    setStreaming(false);
    setConversationId(selectedId);
    if (!selectedId) {
      setMessages([]);
      setLoading(false);
      return;
    }
    const myId = selectedId;
    setLegacyReadFor(null);
    // Cached → paint it now and revalidate silently; uncached → clear + loader.
    const cached = ownerChanged ? undefined : messageCache.get(selectedId);
    if (cached) {
      setMessages(cached);
      setLoading(false);
    } else {
      setMessages([]);
      setLoading(true);
    }

    // Staleness is gated by the ref, not a cleanup-set boolean. React 18
    // StrictMode dev fires the cleanup between effect passes even when
    // the next pass bails on the lastProcessedSelectedIdRef check — a
    // closure-scoped `cancelled = true` flag would then turn the in-flight
    // fetch's `.finally(() => setLoading(false))` into a no-op and leave
    // the ConversationLoader stuck on screen. Using the ref lets the
    // bail logic recognize "no actual selectedId change happened" and
    // commit the response normally.
    apiFetch(`${API_BASE}/api/conversations/${selectedId}/messages`, { credentials: 'include' })
      .then(async (res) => {
        if (lastProcessedSelectedIdRef.current !== myId || previousSelection.current.owner !== owner) return;
        if (!res.ok) {
          // Keep the cached view on a transient revalidation failure.
          if (!cached) {
            const body = await res.text().catch(() => '');
            if (lastProcessedSelectedIdRef.current !== myId || previousSelection.current.owner !== owner) return;
            setError(`${res.status} ${body || res.statusText}`);
          }
          return;
        }
        const data = (await res.json()) as { messages?: Message[]; turn?: TurnActivitySignal };
        if (lastProcessedSelectedIdRef.current !== myId || previousSelection.current.owner !== owner) return;
        setTurnSeed(data.turn ?? null);
        // A send may have started while this silent revalidation of a CACHED
        // thread was in flight (a fast autosend — e.g. the side-chat handoff —
        // fires as soon as the cache paints, before this fetch returns).
        // Overwriting now would wipe the optimistic user bubble and the live
        // stream with the server's pre-turn snapshot; the post-turn consistency
        // check + duplexer reconcile the thread instead. (Same guard the
        // duplexer uses below.)
        if (streamingRef.current) return;
        setMessages(data.messages || []);
      })
      .catch((err: unknown) => {
        if (lastProcessedSelectedIdRef.current !== myId || previousSelection.current.owner !== owner) return;
        if (!cached) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (lastProcessedSelectedIdRef.current !== myId || previousSelection.current.owner !== owner) return;
        setLoading(false);
        setLegacyReadFor(myId);
      });
  }, [selectedId, historyOptions?.enabled, owner]);

  // Refs so the duplexer + visibility-change handlers see latest state without
  // re-subscribing on every render.
  const messagesRef = useRef<Message[]>(messages);
  messagesRef.current = messages;
  const streamingRef = useRef(streaming);
  streamingRef.current = streaming;

  // Mirror the live thread into the shared cache so a later mount for the same
  // conversation paints the latest state. Strip the per-session `animate` flag
  // so the seeded copy renders at once instead of re-running the typewriter.
  useEffect(() => {
    if (!conversationId || loading || historyOptions?.enabled) return;
    messageCache.set(
      conversationId,
      messages.map((m) => (m.animate ? { ...m, animate: false } : m)),
    );
  }, [conversationId, messages, loading, historyOptions?.enabled]);

  useDuplexer((entity: DuplexerEntity) => {
    if (!entity.id) return;
    if (entity.conversationId !== conversationIdRef.current) return;
    const role = entity.role ?? '';
    // Pushes are applied even while a turn streams: a row is published only after it is written, and
    // `messageEnd` carries its `finalId` from that write, so the streamed twin is caught by the id check
    // below (or by `isStreamedDuplicate` if the SSE frame lands late). Rows from other runs must not wait.
    const entityReactions = (entity.metadata as { reactions?: MessageReaction[] } | undefined)?.reactions;
    const entityChoices = readMessageChoices(entity.metadata);
    const entitySurvey = readMessageSurvey(entity.metadata);
    // Every `metadata.*Card` marker on the pushed row, in one read — see `readChatCards`.
    const entityCards = readChatCards(entity.metadata);
    const entityAttachments = entity.attachments;
    const already = messagesRef.current.some((m) => m.id === entity.id || m.finalId === entity.id);
    // A reaction change republishes the UPDATED message entity — the id is
    // already on screen, so patch that row's reactions in place (any role;
    // everything else on the row is kept). The final assistant row republish
    // also carries `metadata.choices` and the attachments the SSE stream
    // couldn't deliver, so a structured ask and its files land on the streamed
    // bubble here too. The in-band `messageEnd` path is the primary delivery for
    // THIS device; this covers other devices / async replies. All patch helpers
    // no-op if the row vanished between the ref read and the state update.
    if (already) {
      setMessages((prev) => {
        let next = setReactionsById(prev, entity.id!, entityReactions ?? []);
        if (entityChoices) next = patchChoicesById(next, entity.id!, entityChoices);
        if (entitySurvey) next = patchSurveyById(next, entity.id!, entitySurvey);
        next = patchChatCardsById(next, entity.id!, entityCards);
        // An empty list is passed through, not filtered here: the helper decides, because an empty one
        // is meaningful on a row holding only placeholders (the harness retracting superseded
        // skeletons) and meaningless everywhere else.
        if (entityAttachments) next = patchAttachmentsById(next, entity.id!, entityAttachments);
        // A room answer can land first without its spoken marker (streamed, or cached) and be republished
        // with it; unmarked it would read as a reply to the owner.
        if (readSpokenAloud(role, entity.metadata)) {
          next = next.map((m) => ((m.id === entity.id || m.finalId === entity.id) && !m.spokenAloud ? { ...m, spokenAloud: true } : m));
        }
        return next;
      });
      return;
    }
    // A room question is a USER row this browser did not send: the chat needs it to start hiding the room's
    // exchange (lib/meeting-room-rows). A plain USER row is drawn too — the user sent it from their phone or
    // another tab — unless it is this browser's own send, whose server id it does not know yet.
    const fromMeetingRoom = readMeetingRoomFlag(role, entity.metadata);
    const spokenAloud = readSpokenAloud(role, entity.metadata);
    // A "Tell {agent}" note (`metadata.outreachComposer`) is a USER row the SERVER dispatched, so it is drawn
    // live like a meeting-room question — with the marker, which renders its thread chip.
    const outreachComposer = role === 'USER' ? readOutreachComposerMarker(entity.metadata) : undefined;
    const ownSendId = ownUnconfirmedSendId(messagesRef.current, entity);
    const kind = liveChatRowKind(role, fromMeetingRoom, !!outreachComposer, !!ownSendId);
    if (ownSendId) {
      // Our own echo: link it to the bubble already drawn, so it is matched once and no later twin of it can be.
      const persistedId = entity.id;
      setMessages((prev) => prev.map((m) => (m.id === ownSendId && !m.finalId ? { ...m, finalId: persistedId } : m)));
    }
    if (kind === 'ignore') return;
    // Deduped by id only: the content guard below would drop a status line that repeats an earlier one.
    if (kind === 'status') {
      setMessages((prev) =>
        upsertMessageById(prev, { id: entity.id, conversationId: entity.conversationId, role, content: entity.content, createdDate: entity.createdDate }),
      );
      return;
    }
    // A bubble we rendered live over SSE normally carries a `finalId` (its
    // persisted id), so the id checks above dedupe the duplexer twin. But when
    // the SSE `messageEnd` finalId link is lost/raced (a truncated stream), the
    // streamed bubble has no finalId, the id checks miss, and we'd paint a
    // second identical copy the post-turn refetch can no longer collapse. The
    // content guard catches that: an identical role+content line already shown
    // by a streamed bubble (finalId OR still-animating) is the same message.
    // History-loaded rows (neither flag) are excluded, so genuinely repeated
    // content from other turns is never dropped. See lib/message-dedup.ts.
    // Assistant rows only, plus the server-originated USER rows that always went through it: another
    // device's USER row is never a streamed twin, and the guard would drop a repeated "ok" from the phone.
    const fromOtherDevice = role === 'USER' && !fromMeetingRoom && !outreachComposer;
    if (!fromOtherDevice && isStreamedDuplicate(messagesRef.current, entity)) return;
    // The duplexer push bypasses the entity converter, so the precedence is applied here.
    const attachments = duplexerAttachments(entity);
    // "Finish Onboarding" (af_tutorial_completion) fires the moment a workspace milestone
    // arrives LIVE on this channel — the duplexer only pushes genuinely new pushes (history
    // replay goes through the messages fetch, never here), and the once-latch keeps it to a
    // single report per browser.
    if (isFirstWorkspaceMilestone(entityCards)) reportAppsFlyerOnboardingComplete();
    // Per-turn agent attribution — mirror the history path so a live round-table
    // turn renders as its authoring agent's group row (not the generic Maestro).
    const agentId = (entity.metadata as { agentId?: string } | undefined)?.agentId;
    const agentMeetingSummary = readMeetingSummaryFlag((entity.metadata as { agentMeetingSummary?: unknown } | undefined)?.agentMeetingSummary);
    const replyTo = (entity.metadata as { replyTo?: Message['replyTo'] } | undefined)?.replyTo;
    // A failed turn's apology arrives here live — the stream that failed is already closed by then.
    const systemError = readSystemErrorFlag(entity.metadata);
    // Upsert rather than blind-append: if the row landed between the ref read
    // above and this update, patch its reactions instead of duplicating.
    setMessages((prev) =>
      upsertMessageById(prev, {
        id: entity.id,
        conversationId: entity.conversationId,
        role: entity.role,
        content: entity.content,
        createdDate: entity.createdDate,
        // Not on another device's USER row: `animate` marks a row as streamed, and `isStreamedDuplicate`
        // would then read the phone's next identical message as this one's twin.
        animate: !fromOtherDevice,
        ...(attachments && attachments.length > 0 ? { attachments } : {}),
        // Every `metadata.*Card` marker the push carried — the harness-written presentation cards and
        // the ones that ride a reply alike. Absent keys are omitted, so this adds only what arrived.
        ...entityCards,
        ...(agentId ? { agentId } : {}),
        ...(agentMeetingSummary ? { agentMeetingSummary } : {}),
        ...(replyTo ? { replyTo } : {}),
        ...(entityReactions && entityReactions.length > 0 ? { reactions: entityReactions } : {}),
        ...(entityChoices ? { choices: entityChoices } : {}),
        ...(entitySurvey ? { survey: entitySurvey } : {}),
        ...(systemError ? { systemError } : {}),
        ...(fromMeetingRoom ? { fromMeetingRoom } : {}),
        ...(spokenAloud ? { spokenAloud } : {}),
        ...(outreachComposer ? { outreachComposer } : {}),
      }),
    );
  });

  // Tab visibility safety net — mirrors mobile's `onForeground → refreshMessages`
  // pattern. The duplexer-js library auto-reconnects on transient drops, but
  // if the tab was hidden long enough that the connection died irrecoverably,
  // a single refetch on focus reconciles any missed messages.
  useEffect(() => {
    const onVisibility = () => {
      if (historyRef.current.enabled) return;
      if (document.visibilityState !== 'visible') return;
      const id = conversationIdRef.current;
      if (!id) return;
      apiFetch(`${API_BASE}/api/conversations/${id}/messages`, { credentials: 'include' })
        .then(async (res) => {
          if (!res.ok) return;
          const data = (await res.json()) as { messages?: Message[]; turn?: TurnActivitySignal };
          if (conversationIdRef.current === id) setTurnSeed(data.turn ?? null);
          const fresh = data.messages || [];
          if (fresh.length === 0) return;
          if (conversationIdRef.current !== id) return;
          // Same hazard as the load-effect guard above: a reply may still be
          // streaming (or just finished but not yet reflected in this GET's
          // snapshot) when the tab regains visibility. Overwriting now would
          // wipe the live/just-finished bubble — the post-turn consistency
          // check + duplexer reconcile the thread instead.
          if (streamingRef.current) return;
          if (fresh.length < messagesRef.current.length) return;
          setMessages(fresh);
        })
        .catch(() => {
          // best-effort — duplexer is still primary
        });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Single-flight: two concurrent sends (or a send racing the onboarding autostart)
  // must share ONE create — each POST mints a new conversation server-side, so a race
  // here is how a user ends up with duplicate (and empty orphan) conversations.
  const createConversationOnce = useRef(createSingleFlight<string | null>()).current;
  const ensureConversation = useCallback(async (workspaceId?: string): Promise<string | null> => {
    const existing = conversationIdRef.current;
    if (existing) return existing;
    return createConversationOnce(async () => {
      // Re-check inside the flight: a conversation may have been selected/created
      // between the caller's check and this run starting.
      const current = conversationIdRef.current;
      if (current) return current;
      const created = await apiFetch(`${API_BASE}/api/conversations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(conversationCreateBody(workspaceId)),
      });
      if (!created.ok) {
        const body = await created.text().catch(() => '');
        throw new Error(`${created.status} ${body || created.statusText}`);
      }
      const data = (await created.json()) as { conversation?: { id?: string } };
      const newId = data.conversation?.id;
      if (!newId) throw new Error('create_failed: server returned no id');
      // Mark the upcoming parent-driven selectedId update as ours so the
      // message-load effect bails when it sees that selectedId === newId.
      expectedSelectedIdAfterCreateRef.current = newId;
      historyRef.current.adopt(newId);
      setConversationId(newId);
      onConversationCreated(newId);
      return newId;
    });
  }, [createConversationOnce, onConversationCreated]);

  const appendMessage = useCallback((message: Message) => {
    if (!message.id) return;
    if (message.conversationId && message.conversationId !== conversationIdRef.current) return;
    setMessages((prev) => (prev.some((m) => m.id === message.id || m.finalId === message.id) ? prev : [...prev, message]));
  }, []);

  // Full re-sync of the active conversation from the server. Used after a voice
  // call ends: ask_orion turns (the user's <voice> request and the harness
  // answer) are persisted server-side but not all reach the live chat — the
  // duplexer never pushes USER-role messages, and on localhost it's offline
  // entirely — so the transcript can be incomplete until a refetch.
  const reload = useCallback(async () => {
    if (historyRef.current.enabled) return historyRef.current.reload();
    const id = conversationIdRef.current;
    if (!id) return;
    try {
      const res = await apiFetch(`${API_BASE}/api/conversations/${id}/messages`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = (await res.json()) as { messages?: Message[]; turn?: TurnActivitySignal };
      if (conversationIdRef.current !== id) return;
      setTurnSeed(data.turn ?? null);
      setMessages(data.messages || []);
    } catch {
      // Best-effort sync; the visibility-change refetch is a further backstop.
    }
  }, []);

  // Toggle the user's own reaction on a message: apply locally at once, POST the
  // toggle, then reconcile from the response (the server array wins) — roll back
  // to the pre-toggle entries on failure. Reactions are in-app annotations, so
  // this works on locked (WhatsApp/SMS-owned) conversations too.
  const toggleReaction = useCallback((message: Message, emoji: string) => {
    const messageId = message.finalId ?? message.id;
    const targetConversationId = message.conversationId || conversationIdRef.current;
    if (!messageId || messageId.startsWith('optimistic-') || !targetConversationId) return;
    const row = messagesRef.current.find((m) => m.id === messageId || m.finalId === messageId);
    const before = row?.reactions ?? [];
    setMessages((prev) => setReactionsById(prev, messageId, toggleReactionEntry(before, emoji, OWN_REACTION_BY)));
    void (async () => {
      try {
        const res = await apiFetch(`${API_BASE}/api/message-reactions`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ conversationId: targetConversationId, messageId, emoji }),
        });
        if (!res.ok) throw new Error(`${res.status}`);
        const data = (await res.json()) as { reactions?: MessageReaction[] };
        setMessages((prev) => setReactionsById(prev, messageId, data.reactions ?? []));
      } catch {
        setMessages((prev) => setReactionsById(prev, messageId, before));
      }
    })();
  }, []);

  const send = useCallback(
    async (
      text: string,
      attachments?: MessageAttachment[],
      references?: MessageReferences,
      replyTo?: Message['replyTo'],
      choiceAnswer?: ChoiceAnswer,
      context?: string,
      surveyAnswer?: SurveyAnswer,
      workspaceId?: string,
    ) => {
      const trimmed = text.trim();
      const hasAttachments = !!attachments && attachments.length > 0;
      if ((!trimmed && !hasAttachments) || streaming) return;
      const sendOwner = previousSelection.current.owner;
      turnEpochRef.current += 1;
      turnCardsRef.current = { cards: {}, adopted: [] };
      setEarlySurvey(undefined);
      setSurveyWriting(false);

      // If a suggested-task chip prefilled this message, record that the owner acted on it so it stops
      // coming back. HERE and not in `MessagesPanel.submitMessage`, because that is only one of four
      // paths into this function — the home-launcher autosend, a voice note and a choice-chip answer all
      // arrive without it. Fire-and-forget; see lib/suggested-task-ack.ts.
      flushSuggestedTaskAck();

      // AppsFlyer send-choke: the FIRST send before the workspace milestone is onboarding-start,
      // the first send after it is active-user — each latched once per browser. Onboarding-finished
      // is the web funnel's own signal (the workspace-milestone latch), not a new source.
      // Force the person id onto the SDK BEFORE these send events fire, rather than relying on
      // AppFrame's /api/me having already resolved. Non-blocking — the send proceeds below; only the
      // AppsFlyer report waits on identity. The finished verdict is captured NOW so the deferred
      // report still classifies THIS send (onboarding-start vs active-user); fireOnce latching in
      // reportAppsFlyerSend keeps it single even if two sends race before ensureMe resolves.
      const onboardingFinished = hasFired('finish');
      void ensureMe().then(() => reportAppsFlyerSend(onboardingFinished));
      // The GTM activation funnel's send-driven milestones (start / active-user). Here
      // rather than in the composer so every send path counts — see lib/gtm-funnel.ts.
      reportFunnelSend();

      // Snapshot the last assistant id BEFORE we touch state. The
      // consistency check uses this to tell "server has produced a NEW
      // assistant for this turn" from "server's last assistant is still the
      // previous turn's reply (so the new one hasn't been persisted yet,
      // keep waiting)."
      const lastAssistantBefore = (() => {
        for (let i = messagesRef.current.length - 1; i >= 0; i--) {
          const r = messagesRef.current[i].role;
          if (r === 'ASSISTANT' || r === 'ASSISTANT_STEP') return messagesRef.current[i];
        }
        return undefined;
      })();
      const preTurnAssistantIds = new Set<string>();
      if (lastAssistantBefore?.id) preTurnAssistantIds.add(lastAssistantBefore.id);
      if (lastAssistantBefore?.finalId) preTurnAssistantIds.add(lastAssistantBefore.finalId);
      // The rows held before the send, so the reconcile can pick out the server's copy of the sent row.
      const preTurnRows = messagesRef.current;

      // Render the optimistic user bubble immediately — even on first send
      // (where the create POST hasn't returned yet). The bubble shouldn't wait
      // 100-300ms for the server round-trip.
      const optimisticUserId = `optimistic-${Date.now()}-user`;
      const optimisticUser: Message = {
        id: optimisticUserId,
        conversationId: conversationId ?? '',
        role: 'USER',
        content: trimmed,
        ...(hasAttachments ? { attachments } : {}),
        ...(replyTo ? { replyTo } : {}),
        // Stamped locally so the tapped chip reads selected at once — selection
        // derives from USER rows carrying a matching choiceAnswer.
        ...(choiceAnswer ? { choiceAnswer } : {}),
        // Same reason for the survey card: the optimistic row's surveyAnswer settles the card at once.
        ...(surveyAnswer ? { surveyAnswer } : {}),
        outreachId: readOutreachReplyScope(references),
      };
      setMessages((prev) => [...prev, optimisticUser]);
      setStreaming(true);
      setError(null);

      const rollbackOptimistic = (message: string) => {
        setMessages((prev) => prev.filter((m) => m.id !== optimisticUserId));
        setStreaming(false);
        setError(message);
      };

      let activeConversationId: string | null = conversationId;
      try {
        activeConversationId = await ensureConversation(workspaceId);
      } catch (err: unknown) {
        rollbackOptimistic(err instanceof Error ? err.message : String(err));
        return;
      }
      // Bump this conversation to the top of the sidebar the moment the
      // user sends — mirrors mobile, which gets the same effect for free
      // via the CONVERSATION duplexer channel firing on every update. The
      // web client only sees ASSISTANT updates on its narrower channel,
      // so the bump has to happen optimistically here.
      if (activeConversationId) bumpConversation(activeConversationId);
      if (!activeConversationId) {
        rollbackOptimistic('create_failed: no conversation');
        return;
      }

      const abort = new AbortController();
      abortRef.current = abort;

      try {
        const res = await apiFetchStream(`${API_BASE}/api/conversations/${activeConversationId}/stream`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messageText: trimmed,
            // This client adopts the conversationId announced on the stream's metadata event (below),
            // so the harness may rescue an id that no longer exists and run the turn in a real one.
            adoptsConversationId: true,
            ...(hasAttachments ? { attachments } : {}),
            ...(references ? { references } : {}),
            ...(replyTo ? { replyTo } : {}),
            ...(choiceAnswer ? { choiceAnswer } : {}),
            ...(surveyAnswer ? { surveyAnswer } : {}),
            ...(context ? { context } : {}),
            // What this client can draw, on EVERY send — see WEB_CLIENT_CAPABILITIES.
            clientCapabilities: WEB_CLIENT_CAPABILITIES,
            deviceTimeZone: browserTimeZone(),
          }),
          signal: abort.signal,
        });
        if (!res.ok || !res.body) {
          const body = await res.text().catch(() => '');
          // Typed rejection from the server-side channel guard: this
          // conversation lives on WhatsApp/SMS and USER sends are refused.
          // Surface it as the locked composer (readOnlyChannel), not a generic
          // red error — the list DTO normally locks the composer up front, so
          // landing here means the client state was stale.
          const lockedChannel = res.status === 403 ? parseChannelReadOnlyError(body) : null;
          if (lockedChannel) {
            setMessages((prev) => prev.filter((m) => m.id !== optimisticUserId));
            setStreaming(false);
            setReadOnlyChannel(lockedChannel);
            abortRef.current = null;
            return;
          }
          throw new Error(`${res.status} ${body.slice(0, 200)}`);
        }

        const parser = createSseParser((_eventName, dataJson) => {
          let ev: StreamEventEnvelope;
          try {
            ev = JSON.parse(dataJson);
          } catch {
            return;
          }
          // The turn may be running in a DIFFERENT conversation than we asked for. A conversation id
          // that no longer exists is rescued server-side into a real one, and this event announces it.
          // Adopting is not optional: without it the reply lands in a conversation this tab never
          // points at, and — because we would keep sending the dead id — every later send would mint
          // another one. `startOnboarding` has always done this; `send` did not, which is why the
          // request only recently began claiming the capability. Reassigning `activeConversationId`
          // also carries the new id into `targetId` below, so the post-turn reconcile follows the turn.
          if (ev.type === 'metadata') {
            const announced = ev.data?.conversationId as string | undefined;
            if (announced && announced !== activeConversationId) {
              activeConversationId = announced;
              // The pre-stream bump went to the id we now know is dead. Move it, or the sidebar holds a
              // top-of-list entry for a conversation that does not exist while the real one sits where
              // it was. (Android moves its pending-turn entry across on adoption for the same reason.)
              bumpConversation(announced);
              // Same handshake as ensureConversation: claim the parent's upcoming selectedId change as
              // ours, adopt locally, and have the URL stamped — without aborting the stream.
              expectedSelectedIdAfterCreateRef.current = announced;
              historyRef.current.adopt(announced);
              setConversationId(announced);
              onConversationCreated(announced);
            }
          }
          applyStreamEvent(ev, setMessages, setError, turnCardsRef.current, onSurveyEvent);
        });

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          parser.push(decoder.decode(value, { stream: true }));
        }
        parser.flush();
      } catch (err: unknown) {
        const isAbort = err instanceof Error && err.name === 'AbortError';
        if (!isAbort) setError(err instanceof Error ? err.message : String(err));
      }
      abortRef.current = null;
      // Hand the survey back to the rows. By now the ask is either stamped on a reply (messageEnd
      // carried it in-band, same askId) — in which case the card does not so much as blink — or it is
      // one the turn offered and then abandoned, and leaving the early copy up would hold the composer
      // for a question nobody is waiting on an answer to.
      setEarlySurvey(undefined);
      setSurveyWriting(false);
      // The turn is over for the CALLER the moment the stream closes: the reply, and any choice chips
      // it offered, are on screen and being read. What follows is reconciliation with the server, and
      // it used to run with `streaming` still true — which `send()` refuses to send through, so every
      // chip tap and every typed message in that window was dropped in silence. Measured on a real
      // thread: the same chip, pressed twice without moving, bailed at `streaming=true` and went
      // through at `streaming=false`. The window is at least 2.5s (the loop below waits before its
      // first poll) and reaches ~17.5s whenever the duplexer push never lands.
      setStreaming(false);
      // Everything below may now race a turn the caller starts while it runs, so it adopts nothing
      // once it has been superseded.
      const reconcileEpoch = turnEpochRef.current;
      const stillOurs = () => conversationIdRef.current === targetId && turnEpochRef.current === reconcileEpoch
        && previousSelection.current.owner === sendOwner;

      // Consistency check — fires after every turn ends. Compares server's
      // last assistant content to ours; replaces if server has more.
      // Catches: zero-content turns, truncated streams, slow multi-iteration
      // replies. Stops early once we agree with the server (and have at
      // least some content), so happy turns pay only one fetch.
      //
      // In production, duplexer's live push usually arrives before our
      // first attempt, so the very first check is server == local → no-op.
      // On localhost the WS is unreachable (ws://); retries cover us.
      const targetId = activeConversationId;
      const lastAssistant = (list: Message[]) => {
        for (let i = list.length - 1; i >= 0; i--) {
          const r = list[i].role;
          if (r === 'ASSISTANT' || r === 'ASSISTANT_STEP') return list[i];
        }
        return undefined;
      };
      // "Server has produced this turn's reply" ⇔ server's last assistant id
      // is NOT one of the ids we knew before the turn started. Length-only
      // checks were wrong: pre-turn last_assistant could exactly match length
      // with server's same pre-turn last_assistant, and we'd exit early
      // while server hasn't actually persisted the new turn yet.
      const isNewTurnReply = (m?: Message): boolean => !!m?.id && !preTurnAssistantIds.has(m.id);
      // Skip the consistency check entirely when the user pressed Stop —
      // they cut the turn short on purpose; comparing server's "what the
      // assistant was going to say" against the partial local content
      // would just snap the bubble forward against the user's intent.
      // ...but the USER's own row is a different matter. It is theirs, the server already holds it, and
      // pressing Stop is no reason to leave it missing until a reload — so when a stop happened the loop
      // still runs for the row repair below and breaks before any assistant reconciliation.
      const userStopped = userStoppedRef.current;
      if (userStopped) userStoppedRef.current = false;
      for (const wait of [2500, 5000, 10000]) {
        await new Promise((r) => setTimeout(r, wait));
        if (!stillOurs()) return;
        try {
          let fresh: Message[];
          if (historyRef.current.enabled) fresh = await historyRef.current.fetchLatest(targetId);
          else {
            const res = await apiFetch(`${API_BASE}/api/conversations/${targetId}/messages`, { credentials: 'include' });
            if (!res.ok) continue;
            const data = (await res.json()) as { messages?: Message[] };
            fresh = data.messages || [];
          }
          // The row the USER sent does not depend on the reply: the server persists it when the stream
          // request opens. So put it back HERE, above the `isNewTurnReply` gate — the gate waits for this
          // turn's assistant reply, and a long research turn does not produce one inside the loop's
          // ~17.5s, which is how a seed lost to the create/select race stayed missing until a manual
          // reload ("Resolve with Maestro opens the chat, nothing is sent, and it loads forever" — the
          // report was intermittent purely because fast replies let the adopt below repair it in time).
          // Restores the one row rather than adopting the page, so a reply we have already streamed is
          // never deleted by a server copy that lacks it — see lib/turn-seed-repair.ts.
          const repaired = restoreSentRow(messagesRef.current, fresh, trimmed);
          if (repaired && stillOurs()) setMessages(repaired);
          // A stopped turn gets the row back and nothing else — see the guard above.
          if (userStopped) break;
          const serverLast = lastAssistant(fresh);
          if (!isNewTurnReply(serverLast)) {
            // A solo reaction is the whole reply and writes no assistant row, so put the server's 👍 (and the
            // persisted id, for later duplexer patches) on our optimistic row. Keep polling: a reaction can
            // also lead real work whose reply this loop must still adopt. See `reactionAnsweredRow`.
            const reacted = reactionAnsweredRow(fresh, preTurnRows, trimmed);
            if (reacted && stillOurs()) {
              // The reaction IS this turn's reply, so a cut stream's error is as stale as on the path below.
              setError(null);
              setMessages((prev) => prev.map((m) => (m.id === optimisticUserId ? { ...m, finalId: reacted.id, reactions: reacted.reactions } : m)));
            }
            continue;
          }
          // The server persisted this turn's reply, so the turn completed even if the SSE was cut
          // mid-stream (which sets `error` above). Clear it — a finished turn must never show a
          // stray technical error beside the answer the agent produced.
          setError(null);
          const ourLast = lastAssistant(messagesRef.current);
          const ourLen = isNewTurnReply(ourLast) ? ourLast?.content?.length ?? 0 : 0;
          const serverLen = serverLast?.content?.length ?? 0;
          // Adopt the server thread when it has a longer reply (a truncated or
          // slow stream) OR simply more messages than we hold. The latter
          // restores a USER bubble dropped during a new-conversation handoff:
          // the duplexer never pushes USER rows, so a sender's own message that
          // was lost in the create/select race would otherwise stay missing
          // until a manual refresh.
          const serverHasMore = fresh.length > messagesRef.current.length;
          // A block whose `messageEnd` never arrived — the stream was cut mid-turn. Neither length
          // test can see it in a MULTI-BLOCK turn: if block 1 orphans while block 2 closes cleanly,
          // `lastAssistant` compares block 2 (equal on both sides) and, once the duplexer twin of
          // the orphan has been appended, our list is the LONGER one — so the turn reconciled to
          // "we agree with the server" while holding a half-written bubble beside a duplicate of it.
          // The server's copy of the row is complete, so adopt the thread. Deliberately an ADOPT and
          // not a prune: dropping the row outright deletes prose the reader has already read, which
          // is the regression #8935 is about.
          const weHoldAnOrphan = messagesRef.current.some(isOrphanedStreamBlock);
          if ((serverLen > ourLen || serverHasMore || weHoldAnOrphan) && stillOurs()) {
            if (historyRef.current.enabled) await historyRef.current.reload();
            else setMessages(fresh);
          }
          break;
        } catch {
          // best-effort — try next attempt
        }
      }

      if (stillOurs()) onTurnComplete();
    },
    [conversationId, streaming, ensureConversation, onTurnComplete],
  );

  // Mid-stream steer: fold a message into the RUNNING turn instead of starting a new one. Persist
  // it via the push endpoint (no new stream, no abort) — the harness folds the row into the open
  // streamMessage turn, which keeps responding. `streaming` is left untouched: the in-flight send()
  // owns it, and its end-of-turn consistency refetch reconciles this row (serverHasMore restores it
  // if the optimistic bubble raced the reconcile). A no-op before a conversation exists — there is
  // no running turn to steer into.
  const steer = useCallback(async (text: string, attachments?: MessageAttachment[]) => {
    const trimmed = text.trim();
    const hasAttachments = !!attachments && attachments.length > 0;
    if (!trimmed && !hasAttachments) return;
    const targetId = conversationIdRef.current;
    if (!targetId) return;
    // A steer adds a USER row the server has not persisted yet, so it must invalidate an in-flight
    // reconciliation for the same reason a fresh send does.
    turnEpochRef.current += 1;

    // Same as send(): guarantee the CUID is on the SDK before the active-user/onboarding-start
    // event fires. Non-blocking; verdict captured now (see the note in send()).
    const onboardingFinished = hasFired('finish');
    void ensureMe().then(() => reportAppsFlyerSend(onboardingFinished));
    reportFunnelSend();
    const optimisticUserId = `optimistic-${Date.now()}-user-steer`;
    const optimisticUser: Message = {
      id: optimisticUserId,
      conversationId: targetId,
      role: 'USER',
      content: trimmed,
      ...(hasAttachments ? { attachments } : {}),
    };
    setMessages((prev) => [...prev, optimisticUser]);
    bumpConversation(targetId);

    const rollback = () => setMessages((prev) => prev.filter((m) => m.id !== optimisticUserId));
    try {
      const res = await apiFetch(`${API_BASE}/api/conversations/${targetId}/push`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messageText: trimmed,
          ...(hasAttachments ? { attachments } : {}),
        }),
      });
      if (!res.ok) {
        // A stale-client read-only send (403) locks the composer like send() does; anything else
        // just drops the optimistic bubble — the running turn is unaffected either way.
        if (res.status === 403) {
          const lockedChannel = parseChannelReadOnlyError(await res.text().catch(() => ''));
          if (lockedChannel) setReadOnlyChannel(lockedChannel);
        }
        rollback();
      }
    } catch {
      rollback();
    }
  }, []);

  // Set when the user clicks Stop so the in-flight send() loop knows to
  // skip its consistency-refetch (no point comparing server vs local if
  // the user just cut the turn short) and so `streaming` flips false now
  // instead of after the refetch's 17s of waits.
  const userStoppedRef = useRef(false);
  const stop = useCallback(async () => {
    if (!conversationId || !streaming) return;
    userStoppedRef.current = true;
    setStreaming(false);
    abortRef.current?.abort();
    try {
      // Through the orion-web API too, so the Stop flag is signed for the same
      // metasite the stream runs on — the streaming pair stays on one path.
      await apiFetch(`${API_BASE}/api/conversations/${conversationId}/stop`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch {
      // server-side stop best-effort — local UI already reflects stopped
    }
  }, [conversationId, streaming]);

  // Proactive onboarding greeting. Unlike send(), there's NO optimistic user bubble (the user said
  // nothing) and we do NOT pre-create the conversation — the harness owns the arm decision and only
  // creates + greets for the treatment arm, handing the new id back in the stream's METADATA event.
  // On METADATA we bind to that id with the same ref-dance ensureConversation uses, so the parent's
  // selectedId catch-up doesn't abort the stream or wipe the streaming bubble. The greeting then renders
  // live through the same applyStreamEvent path send() uses. Control / already-onboarded users get a
  // single DONE with no METADATA → nothing renders, we stay put.
  const startOnboarding = useCallback(async (): Promise<string | null> => {
    if (streaming || conversationIdRef.current) return null;
    setStreaming(true);
    setError(null);
    turnCardsRef.current = { cards: {}, adopted: [] };
    setEarlySurvey(undefined);
    setSurveyWriting(false);
    const abort = new AbortController();
    abortRef.current = abort;
    let newConversationId: string | null = null;
    // The latest page of a conversation, through whichever reader is on; null on a failed legacy read.
    const readOnboardingConversation = async (targetId: string): Promise<Message[] | null> => {
      if (historyRef.current.enabled) return historyRef.current.fetchLatest(targetId);
      const response = await apiFetch(`${API_BASE}/api/conversations/${targetId}/messages`, { credentials: 'include' });
      if (!response.ok) return null;
      const payload = (await response.json()) as { messages?: Message[] };
      return payload.messages || [];
    };
    // Cleared once a conversation is created for it: the intent is about this visit, and the harness has
    // stamped it on the conversation by then.
    // The identity only filters out another account's intent, so it is bounded and fails open: a slow
    // `/api/me` must never hold back the greeting.
    const me = await Promise.race([ensureMe().catch(() => null), new Promise<null>((resolve) => setTimeout(() => resolve(null), 1500))]);
    const landingIntent = loadLandingIntent(me?.userId ?? '');
    try {
      const res = await apiFetchStream(`${API_BASE}/api/onboarding/start`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(landingIntent ? { landingIntent } : {}),
        signal: abort.signal,
      });
      if (!res.ok || !res.body) {
        setStreaming(false);
        return null;
      }
      // The stream ids we START this turn (messageStart). The reconcile pass below prunes only
      // these when they never finalize — so a duplexer-pushed message (a different, persisted id)
      // can never be mistaken for an orphan, independent of when `streaming` flips.
      const startedStreamIds = new Set<string>();
      const parser = createSseParser((_eventName, dataJson) => {
        let ev: StreamEventEnvelope;
        try {
          ev = JSON.parse(dataJson);
        } catch {
          return;
        }
        if (ev.type === 'messageStart') {
          const startedId = ev.data?.streamId as string | undefined;
          if (startedId) startedStreamIds.add(startedId);
        }
        const id = ev.type === 'metadata' ? (ev.data?.conversationId as string | undefined) : undefined;
        if (id && !newConversationId) {
          newConversationId = id;
          // Same handshake as ensureConversation: claim the upcoming parent selectedId change as ours,
          // set our own id immediately, and tell the parent to stamp the URL — without aborting the stream.
          expectedSelectedIdAfterCreateRef.current = id;
          historyRef.current.adopt(id);
          setConversationId(id);
          onConversationCreated(id);
          if (landingIntent) clearLandingIntent();
        }
        applyStreamEvent(ev, setMessages, setError, turnCardsRef.current, onSurveyEvent);
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        parser.push(decoder.decode(value, { stream: true }));
      }
      parser.flush();
    } catch (err: unknown) {
      const isAbort = err instanceof Error && err.name === 'AbortError';
      if (!isAbort) setError(err instanceof Error ? err.message : String(err));
    }
    // Same hand-back as send(): once the stream is closed the rows own whatever ask survived it.
    setEarlySurvey(undefined);
    setSurveyWriting(false);
    // Reconcile a stream that ended WITHOUT finalizing its assistant bubble — e.g. the upstream
    // SSE was cut mid-turn (a server timeout writes a raw error and closes). That leaves an
    // orphaned, half-typed bubble (animate, no finalId) that the duplexer dedupe (keys on finalId)
    // can't collapse, so it lingers beside the full greeting the duplexer later delivers. Drop the
    // orphan; then, only if nothing supplies the greeting (the duplexer normally does), recover the
    // harness-persisted copy so it still lands as exactly one bubble. A clean stream has no orphan,
    // so this whole block is a no-op on the happy path. Mirrors send()'s post-stream consistency pass.
    if (messagesRef.current.some(isOrphanedStreamBlock)) {
      setMessages((prev) => prev.filter((m) => !isOrphanedStreamBlock(m)));
      if (newConversationId && !abort.signal.aborted) {
        const targetId = newConversationId;
        const hasAssistant = (list: Message[]) => list.some((m) => m.role === 'ASSISTANT' || m.role === 'ASSISTANT_STEP');
        for (const wait of [1500, 3500, 7000]) {
          await new Promise((r) => setTimeout(r, wait));
          // Stop if the user navigated away, or the duplexer already landed the greeting.
          if (conversationIdRef.current !== targetId || hasAssistant(messagesRef.current)) break;
          try {
            const fresh = await readOnboardingConversation(targetId);
            if (!fresh) continue;
            if (hasAssistant(fresh) && conversationIdRef.current === targetId && !hasAssistant(messagesRef.current)) {
              setMessages(fresh);
              break;
            }
          } catch {
            // best-effort — try the next attempt
          }
        }
      }
    }
    // The harness may answer with a conversation that ALREADY exists, one a pack prepare wrote with its
    // greeting and card, and stream nothing into it (metadata, then done). The adoption above marks it
    // as painted, so nothing else reads it: the legacy read skips an id this client announced, and the
    // history reader only revalidates once it is enabled. Read it once here, as the recovery above does,
    // so the conversation shows without a reload. A greeting this stream painted skips the read.
    if (newConversationId && !abort.signal.aborted && messagesRef.current.length === 0) {
      const adoptedId = newConversationId;
      const existing = await readOnboardingConversation(adoptedId).catch(() => null);
      if (existing && existing.length > 0 && conversationIdRef.current === adoptedId && messagesRef.current.length === 0) {
        setMessages(existing);
      }
    }
    // Same reconciliation as send(): if the assistant reply is now present (streamed,
    // duplexer-delivered, or recovered above), a mid-stream cut was benign — clear its error
    // so a completed onboarding turn never shows a stray technical error.
    if (messagesRef.current.some((m) => m.role === 'ASSISTANT' || m.role === 'ASSISTANT_STEP')) {
      setError(null);
    }
    abortRef.current = null;
    setStreaming(false);
    return newConversationId;
  }, [streaming, onConversationCreated]);

  // Our own stream owns the indicator while it runs, so the remote signal is only consulted outside
  // it — otherwise the tail of a send (the stamp outlives the SSE close by a beat) would double up.
  const remoteTurn = useRemoteTurn(conversationId, turnSeed);
  const remoteTurnActive = remoteTurn && !streaming;

  return {
    history,
    conversationId,
    loading,
    readSettled: history.enabled ? history.loaded : !!selectedId && legacyReadFor === selectedId,
    streaming,
    remoteTurnActive,
    error,
    readOnlyChannel,
    messages,
    earlySurvey,
    surveyWriting,
    send,
    steer,
    stop,
    ensureConversation,
    appendMessage,
    reload,
    toggleReaction,
    startOnboarding,
  };
}

// Exported for `test/chat-step-stays-visible.test.ts`: the demotion this fix is about happens at
// `messageEnd`, and driving it through the real event objects is the only way to test the live half
// without mounting the hook.
export function applyStreamEvent(
  ev: StreamEventEnvelope,
  setMessages: Dispatch<SetStateAction<Message[]>>,
  setError: Dispatch<SetStateAction<string | null>>,
  turnCards?: { cards: ChatCards; adopted?: string[] },
  onSurvey?: (event: { survey?: MessageSurvey; writing?: boolean }) => void,
) {
  const data = ev.data ?? {};
  switch (ev.type) {
    // The turn's survey ask, before any row carries it — see `earlySurvey` in the hook for why it is
    // handed over early and who owns it afterwards. `data.survey` is the bare MessageSurvey; wrap it
    // so the shared metadata-shaped sanitizer validates it exactly as the messageEnd copy is.
    case 'survey': {
      // The `writing` arm carries no questions — it says the model has started typing them.
      if ((data as { writing?: unknown }).writing === true) {
        onSurvey?.({ writing: true });
        return;
      }
      const survey = readMessageSurvey({ survey: (data as { survey?: unknown }).survey });
      if (survey) onSurvey?.({ survey });
      return;
    }
    // A card the turn has produced but not yet stamped on a reply. It is held until a block opens and
    // handed to that block below, rather than rendered on its own: a card is drawn ABOVE the words
    // about it, so arriving with the bubble's first character means nothing is ever inserted above
    // text the reader already watched appear. `messageEnd` still carries the durable copy.
    case 'cards': {
      if (!turnCards) return;
      turnCards.cards = { ...turnCards.cards, ...readChatCards(data) };
      return;
    }
    // A card that is its OWN row, already persisted — appended here rather than left to the duplexer,
    // whose pushes are dropped outright while a turn streams (see the `streamingRef` guard on the push
    // handler). Without this the row could not render until the turn ended, and the card then appeared
    // above a reply the reader had already finished. Keyed on the persisted id so the post-turn refetch
    // and the duplexer republish of the same row cannot append a second copy.
    case 'cardRow': {
      const id = data.id as string | undefined;
      if (!id) return;
      const cards = readChatCards(data);
      if (Object.keys(cards).length === 0) return;
      let appended = false;
      setMessages((prev) => {
        if (prev.some((m) => m.finalId === id || m.id === id)) return prev;
        appended = true;
        return [...prev, { id, finalId: id, role: 'ASSISTANT', content: (data.content as string) ?? '', ...cards }];
      });
      // ⚠️ The onboarding report has to fire HERE now, and only on a genuine append. It used to hang off
      // the duplexer push, but this event beats that push to the row — and the push's `already` branch
      // patches and RETURNS before reporting, so leaving it there loses the report entirely. Gated on
      // `appended` so the push's own call and this one cannot both count the same row (the once-latch
      // inside would hold anyway; this keeps the intent local rather than relying on it).
      if (appended && isFirstWorkspaceMilestone(cards)) reportAppsFlyerOnboardingComplete();
      return;
    }
    case 'messageStart': {
      const streamId = data.streamId as string | undefined;
      if (!streamId) return;
      // Guard against a re-delivered start for a streamId already on screen:
      // a blind append would paint a second bubble sharing the same React key,
      // and messageDelta (which maps ALL rows matching streamId) would feed both.
      setMessages((prev) => {
        if (prev.some((m) => m.id === streamId)) return prev;
        // ⚠️ Opening a block CLOSES every other one. `streamOpen` is deliberately left true on a row
        // whose `messageEnd` never arrived (a cut stream), and without this that row stays "open" for
        // the rest of the session — so the next turn's live gate would put a caret back on it and hide
        // its copy button, on a message the reader finished reading minutes ago. The harness streams
        // blocks one at a time (a step, then the reply), so at most one row is ever genuinely open;
        // this makes that invariant true in the state rather than assumed.
        const closed = prev.map((m) => (m.streamOpen ? { ...m, streamOpen: false } : m));
        // The turn's early-delivered cards ride this block from its first frame — see the `cards` case.
        const early = turnCards?.cards ?? {};
        if (turnCards && Object.keys(early).length > 0) {
          turnCards.adopted = [...(turnCards.adopted ?? []), streamId];
        }
        return [...closed, { id: streamId, role: data.role, content: '', animate: true, streamOpen: true, ...early }];
      });
      return;
    }
    case 'messageDelta': {
      const streamId = data.streamId as string | undefined;
      const delta = data.deltaText as string | undefined;
      if (!streamId || !delta) return;
      setMessages((prev) => prev.map((m) => (m.id === streamId ? { ...m, content: (m.content || '') + delta } : m)));
      return;
    }
    case 'messageEnd': {
      const streamId = data.streamId as string | undefined;
      const finalId = data.finalId as string | undefined;
      if (!streamId) return;
      // A block closing stands the survey loader down, whatever else this event carries. Not every
      // announced ask arrives: chips outrank a survey on one reply, and the harness drops an offer that
      // real work followed — and the ask, when it does arrive, is handed over BEFORE the row that
      // carries it is persisted, so by this point it has either come or it never will. Without this the
      // loader would promise questions for the rest of a turn that decided not to ask them.
      onSurvey?.({ writing: false });
      // Choices ride the End event in-band (like THINKING rows) so a streamed bubble renders its
      // chips immediately — no dependency on the slower duplexer republish, which races messageEnd
      // and, on loss, leaves the ask absent until a reload. `data.choices` is the bare MessageChoices
      // object; wrap it so the shared metadata-shaped sanitizer validates it.
      const endChoices = readMessageChoices({ choices: (data as { choices?: unknown }).choices });
      // The survey rides the End event in-band for the same reason as choices — `data.survey` is the
      // bare MessageSurvey; wrap it so the shared metadata-shaped sanitizer validates it.
      const endSurvey = readMessageSurvey({ survey: (data as { survey?: unknown }).survey });
      // The row's cards ride the End event in-band for the same reason as choices, and it matters
      // more: several of them ARE the action — the reply text deliberately carries no connect URL, the
      // outreach preview is the words being approved — so a bubble that missed one asks the user to act
      // on something they cannot see until a cold reload.
      //
      // Read as a whole rather than card by card. The harness spreads the persisted row's card metadata
      // onto `data` under the SAME keys the metadata uses, so the End payload IS a metadata object as
      // far as the readers are concerned — which is what lets a new card kind arrive with no change
      // here. See `readChatCards`.
      const endCards = readChatCards(data);
      // A band's create turn announces the first workspace on its REPLY row rather than as a card row, so
      // the onboarding report is made here too (latched once per browser; this event is live-only).
      if (endCards.bandCreated) reportAppsFlyerOnboardingComplete();
      // Attachments ride the End event in-band for the same reason as choices: SSE delivers only text,
      // so without this a streamed reply's files show nothing until a cold reload refetches the row
      // (the duplexer republish that carries them races messageEnd and loses to the streaming guard).
      const endAttachments = (data as { attachments?: MessageAttachment[] }).attachments;
      const hasEndAttachments = Array.isArray(endAttachments) && endAttachments.length > 0;
      // The row the card was PERSISTED on owns it. An early-delivered card was handed to whichever
      // block opened first, which is the announcing reply in an ordinary turn but is a step when the
      // turn went on to call another tool — so the keys this End carries come off the OTHER rows that
      // adopted an early copy, or the card draws twice.
      //
      // Scoped to those rows and not to the whole transcript: card KINDS repeat across turns, so
      // sweeping every row would strip a previous turn's settled reply of its own opportunity card.
      const endCardKeys = Object.keys(endCards) as (keyof ChatCards)[];
      const strippable = new Set((turnCards?.adopted ?? []).filter((id) => id !== streamId));
      if (turnCards && endCardKeys.length > 0) {
        turnCards.cards = Object.fromEntries(Object.entries(turnCards.cards).filter(([key]) => !endCardKeys.includes(key as keyof ChatCards)));
      }
      // Keep streamId as the React key (typewriter doesn't snap-remount) but
      // stash the persisted id so duplexer-pushed entities can dedupe. A push that beat this event
      // already appended the same row under its persisted id; the streamed bubble keeps it.
      setMessages((prev) =>
        prev.filter((m) => !finalId || m.id !== finalId || m.id === streamId).map((m) =>
          m.id !== streamId
            ? strippable.has(m.id ?? '')
              ? endCardKeys.reduce((row, key) => (row[key] === undefined ? row : { ...row, [key]: undefined }), m)
              : m
            : {
                ...m,
                role: data.role ?? m.role,
                finalId,
                // The server is done appending to this row: the caret comes off it here, whether or
                // not the End event carried a persisted id.
                streamOpen: false,
                ...(endChoices ? { choices: endChoices } : {}),
                ...(endSurvey ? { survey: endSurvey } : {}),
                // Absent keys are omitted by `readChatCards`, so this spread adds the cards the End
                // carried and leaves any already on the row alone.
                ...endCards,
                ...(hasEndAttachments ? { attachments: endAttachments } : {}),
              },
        ),
      );
      return;
    }
    case 'error': {
      setError(typeof data.message === 'string' ? data.message : 'stream error');
      return;
    }
    // metadata, done — no UI side-effect for v1
    default:
      return;
  }
}
